import { styled } from "@mui/material/styles";
import React, { createContext, useCallback, useContext, useId, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "./Feedback";
import { ChevronDownIcon, CloseIcon, SearchIcon } from "./icons";
import { focusRing } from "./tokens";
const FieldContext = /*#__PURE__*/ createContext(null);
const FieldRoot = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-1)",
    minWidth: 0
});
const FieldLabel = styled("label")({
    fontSize: "var(--j-fs-sm)",
    lineHeight: "var(--j-lh-sm)",
    color: "var(--j-ink-2)"
});
const Helper = styled("span", {
    shouldForwardProp: (prop)=>prop !== "isError"
})(({ isError })=>({
        fontSize: "var(--j-fs-xs)",
        lineHeight: "var(--j-lh-xs)",
        color: isError ? "var(--j-err)" : "var(--j-ink-3)"
    }));
export function Field({ label, helperText, error = false, id, children, className }) {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const helperId = helperText ? `${fieldId}-helper` : undefined;
    return /*#__PURE__*/ React.createElement(FieldContext.Provider, {
        value: {
            id: fieldId,
            describedBy: helperId,
            isError: error
        }
    }, /*#__PURE__*/ React.createElement(FieldRoot, {
        className: className
    }, /*#__PURE__*/ React.createElement(FieldLabel, {
        htmlFor: fieldId
    }, label), children, helperText && /*#__PURE__*/ React.createElement(Helper, {
        id: helperId,
        isError: error
    }, helperText)));
}
function useFieldProps(props) {
    const field = useContext(FieldContext);
    const isError = props.error ?? field?.isError ?? false;
    return {
        id: props.id ?? field?.id,
        "aria-describedby": props["aria-describedby"] ?? field?.describedBy,
        "aria-invalid": isError || undefined,
        isError
    };
}
const inputBase = {
    ...focusRing,
    width: "100%",
    borderRadius: "var(--j-r-sm)",
    background: "var(--j-surface)",
    border: "1px solid var(--j-line-strong)",
    fontSize: "var(--j-fs-md)",
    lineHeight: "var(--j-lh-md)",
    color: "var(--j-ink)",
    transition: "border-color 150ms",
    "&::placeholder": {
        color: "var(--j-ink-3)"
    },
    "&[aria-invalid=true]": {
        borderColor: "var(--j-err)"
    },
    "&:disabled": {
        color: "var(--j-ink-3)",
        borderColor: "var(--j-line)"
    }
};
const InputRoot = styled("input")({
    ...inputBase,
    height: "var(--j-btn-h-lg)",
    padding: "0 var(--j-sp-2)"
});
const TextAreaRoot = styled("textarea")({
    ...inputBase,
    display: "block",
    resize: "none",
    padding: "var(--j-sp-1-5) var(--j-sp-2)"
});
export const TextInput = /*#__PURE__*/ React.forwardRef(function TextInput({ error, ...rest }, ref) {
    const { isError: _isError, ...fieldProps } = useFieldProps({
        ...rest,
        error
    });
    return /*#__PURE__*/ React.createElement(InputRoot, {
        ref: ref,
        ...rest,
        ...fieldProps
    });
});
const DEFAULT_MAX_ROWS = 8;
export const TextArea = /*#__PURE__*/ React.forwardRef(function TextArea({ error, minRows = 3, maxRows = DEFAULT_MAX_ROWS, ...rest }, ref) {
    const { isError: _isError, ...fieldProps } = useFieldProps({
        ...rest,
        error
    });
    const innerRef = useRef(null);
    const setRefs = useCallback((element)=>{
        innerRef.current = element;
        if (typeof ref === "function") ref(element);
        else if (ref) ref.current = element;
    }, [
        ref
    ]);
    const resize = useCallback(()=>{
        const element = innerRef.current;
        if (!element) return;
        const style = getComputedStyle(element);
        const lineHeight = parseFloat(style.lineHeight) || 19;
        const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
        const chrome = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + borders;
        const minHeight = lineHeight * minRows + chrome;
        const maxHeight = lineHeight * maxRows + chrome;
        element.style.height = "auto";
        const contentHeight = element.scrollHeight + borders;
        element.style.height = `${Math.min(Math.max(contentHeight, minHeight), maxHeight)}px`;
        element.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
    }, [
        minRows,
        maxRows
    ]);
    useLayoutEffect(resize, [
        resize,
        rest.value
    ]);
    useLayoutEffect(()=>{
        const element = innerRef.current;
        if (!element) return undefined;
        let width = element.clientWidth;
        const observer = new ResizeObserver(()=>{
            if (element.clientWidth === width) return;
            width = element.clientWidth;
            resize();
        });
        observer.observe(element);
        document.fonts?.ready.then(resize);
        return ()=>observer.disconnect();
    }, [
        resize
    ]);
    return /*#__PURE__*/ React.createElement(TextAreaRoot, {
        ref: setRefs,
        rows: minRows,
        ...rest,
        ...fieldProps,
        onInput: (event)=>{
            resize();
            rest.onInput?.(event);
        }
    });
});
const SearchRoot = styled("div")({
    position: "relative",
    height: "var(--j-btn-h)",
    borderRadius: "var(--j-r-sm)",
    background: "var(--j-surface)",
    border: "1px solid var(--j-line-strong)",
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-1-5)",
    padding: "0 var(--j-sp-1) 0 var(--j-sp-2)",
    color: "var(--j-ink-3)",
    minWidth: 0,
    flexShrink: 0,
    "&:focus-within": {
        outline: "var(--j-focus-w) solid var(--j-accent)",
        outlineOffset: "var(--j-focus-offset)"
    }
});
const SearchInput = styled("input")({
    flex: 1,
    minWidth: 0,
    height: "100%",
    border: 0,
    outline: "none",
    background: "transparent",
    fontSize: "var(--j-fs-md)",
    color: "var(--j-ink)",
    "&::placeholder": {
        color: "var(--j-ink-3)"
    },
    "&::-webkit-search-cancel-button": {
        display: "none"
    }
});
const ClearButton = styled("button")({
    ...focusRing,
    width: "1.5rem",
    height: "1.5rem",
    marginRight: "calc(-1 * var(--j-sp-1-5))",
    flexShrink: 0,
    padding: 0,
    border: 0,
    borderRadius: "var(--j-r-sm)",
    background: "none",
    color: "var(--j-ink-3)",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    transition: "background-color 150ms",
    "& > span": {
        display: "grid",
        placeItems: "center"
    },
    "&:hover": {
        background: "var(--j-hover)",
        color: "var(--j-ink)"
    }
});
const SearchTrail = styled("span")({
    width: "var(--j-tag-h)",
    display: "grid",
    placeItems: "center",
    flexShrink: 0
});
export function SearchField({ value, onChange, placeholder, loading = false, autoFocus, disabled, "aria-label": ariaLabel, className }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const label = placeholder ?? t("search");
    const clear = ()=>{
        onChange("");
        inputRef.current?.focus();
    };
    return /*#__PURE__*/ React.createElement(SearchRoot, {
        className: className,
        role: "search"
    }, /*#__PURE__*/ React.createElement(SearchIcon, {
        size: 14
    }), /*#__PURE__*/ React.createElement(SearchInput, {
        ref: inputRef,
        type: "search",
        value: value,
        placeholder: label,
        "aria-label": ariaLabel ?? label,
        autoFocus: autoFocus,
        disabled: disabled,
        onChange: (event)=>onChange(event.target.value),
        onKeyDown: (event)=>{
            if (event.key === "Escape" && value) {
                event.stopPropagation();
                clear();
            }
        }
    }), loading && /*#__PURE__*/ React.createElement(SearchTrail, null, /*#__PURE__*/ React.createElement(Spinner, {
        size: "sm"
    })), !loading && !disabled && value && /*#__PURE__*/ React.createElement(ClearButton, {
        type: "button",
        "aria-label": t("ui.clearSearch"),
        onClick: clear
    }, /*#__PURE__*/ React.createElement("span", null, /*#__PURE__*/ React.createElement(CloseIcon, {
        size: 12
    }))));
}
const SelectRoot = styled("button")({
    ...focusRing,
    width: "100%",
    height: "var(--j-btn-h-lg)",
    borderRadius: "var(--j-r-sm)",
    background: "var(--j-surface)",
    border: "1px solid var(--j-line-strong)",
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-1-5)",
    padding: "0 var(--j-sp-2)",
    fontSize: "var(--j-fs-md)",
    color: "var(--j-ink)",
    textAlign: "left",
    cursor: "pointer",
    minWidth: 0,
    "& > .sel-value": {
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },
    "& > .sel-value[data-placeholder=true]": {
        color: "var(--j-ink-3)"
    },
    "& > svg": {
        color: "var(--j-ink-3)"
    },
    "&[aria-invalid=true]": {
        borderColor: "var(--j-err)"
    },
    "&:disabled": {
        color: "var(--j-ink-3)",
        borderColor: "var(--j-line)",
        cursor: "default"
    }
});
export const SelectTrigger = /*#__PURE__*/ React.forwardRef(function SelectTrigger({ placeholder, error, children, type = "button", ...rest }, ref) {
    const { isError: _isError, ...fieldProps } = useFieldProps({
        ...rest,
        error
    });
    const hasValue = children !== undefined && children !== null && children !== "";
    return /*#__PURE__*/ React.createElement(SelectRoot, {
        ref: ref,
        type: type,
        "aria-haspopup": "listbox",
        ...rest,
        ...fieldProps
    }, /*#__PURE__*/ React.createElement("span", {
        className: "sel-value",
        "data-placeholder": !hasValue
    }, hasValue ? children : placeholder), /*#__PURE__*/ React.createElement(ChevronDownIcon, {
        size: 14
    }));
});
